import React, { useState, useEffect, useRef } from "react";
import { MessageCircle, X, Send, Smile, Paperclip, FileText, Download } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { BACKEND_URL, API } from "@/config";
import { EmojiPicker } from "./EmojiPicker";

// ==================== CHAT WINDOW COMPONENT ====================
export const ChatWindow = ({ currentUser, currentRole, partnerUsername, partnerName, onClose, isFloating = false }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [isOtherTyping, setIsOtherTyping] = useState(false);
  const [uploading, setUploading] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const prevMessageCountRef = useRef(0);

  const scrollToBottom = (force = false) => {
    const container = messagesContainerRef.current;
    if (!container) return;
    // Only auto-scroll if user is near the bottom or it's forced (new message sent)
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 120;
    if (force || isNearBottom) {
      container.scrollTop = container.scrollHeight;
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/chat/conversation/${partnerUsername}`);
      setMessages(response.data.messages || []);
      const reader = currentRole === 'admin' ? 'admin' : currentUser;
      await axios.put(`${API}/chat/read/${partnerUsername}/${reader}`);
    } catch (err) {
      console.error('Error fetching chat:', err);
    }
  };

  useEffect(() => {
    const checkTyping = async () => {
      try {
        const response = await axios.get(`${API}/chat/typing/${partnerUsername}`);
        if (currentRole === 'admin') {
          setIsOtherTyping(response.data.partner_is_typing);
        } else {
          setIsOtherTyping(response.data.admin_is_typing);
        }
      } catch (err) {}
    };
    const interval = setInterval(checkTyping, 1500);
    return () => clearInterval(interval);
  }, [partnerUsername, currentRole]);

  const sendTypingStatus = (isTyping) => {
    const sender = currentRole === 'admin' ? 'admin' : currentUser;
    axios.post(`${API}/chat/typing`, { username: sender, is_typing: isTyping }).catch(() => {});
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    sendTypingStatus(true);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => sendTypingStatus(false), 3000);
  };

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 3000);
    return () => clearInterval(interval);
  }, [partnerUsername]);

  useEffect(() => {
    // Scroll to bottom only on first load or when new messages arrive
    const newCount = messages.length;
    if (newCount > prevMessageCountRef.current) {
      scrollToBottom(prevMessageCountRef.current === 0);
    }
    prevMessageCountRef.current = newCount;
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || sending) return;
    setSending(true);
    try {
      const recipientUsername = currentRole === 'admin' ? partnerUsername : 'admin';
      await axios.post(`${API}/chat/send`, {
        sender_username: currentRole === 'admin' ? 'admin' : currentUser,
        sender_role: currentRole,
        recipient_username: recipientUsername,
        message: input.trim()
      });
      setInput('');
      setShowEmoji(false);
      sendTypingStatus(false);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      await fetchMessages();
      scrollToBottom(true);
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      alert('Το αρχείο υπερβαίνει τα 10MB');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('sender_username', currentRole === 'admin' ? 'admin' : currentUser);
      formData.append('sender_role', currentRole);
      formData.append('recipient_username', currentRole === 'admin' ? partnerUsername : 'admin');
      await axios.post(`${API}/chat/upload`, formData);
      await fetchMessages();
      scrollToBottom(true);
    } catch (err) {
      alert('Σφάλμα: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const formatTime = (isoString) => {
    const d = new Date(isoString);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const time = d.toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' });
    if (isToday) return time;
    return `${d.toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit' })} ${time}`;
  };

  const containerClass = isFloating
    ? "flex flex-col h-[450px] w-full"
    : "flex flex-col h-[600px] w-full";

  return (
    <div className={containerClass} data-testid="chat-window">
      {/* Header */}
      <div className="bg-gradient-to-r from-navy-200 to-navy-100 border-b border-gold/30 px-4 py-3 flex items-center justify-between rounded-t-xl">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gold/20 flex items-center justify-center">
            <MessageCircle className="w-5 h-5 text-gold" />
          </div>
          <div>
            <p className="text-white font-medium text-sm">{partnerName || partnerUsername}</p>
            <p className="text-slate-400 text-xs">{currentRole === 'admin' ? 'Συνεργάτης' : 'J.HOUSEOFSECRETARY'}</p>
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-700/50 hover:bg-slate-600 flex items-center justify-center text-white transition-colors" data-testid="chat-close-btn">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#0a1628]/50" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-slate-500">
            <MessageCircle className="w-12 h-12 mb-3 opacity-30" />
            <p className="text-sm">Ξεκινήστε τη συνομιλία</p>
          </div>
        )}
        {messages.map((msg, index) => {
          const isMe = (currentRole === 'admin' && msg.sender_role === 'admin') || 
                       (currentRole === 'partner' && msg.sender_username === currentUser);
          return (
            <div key={msg.id || index} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
                isMe 
                  ? 'bg-gradient-to-br from-gold to-amber-600 text-navy-200'
                  : 'bg-navy-100 border border-slate-700/50 text-white'
              }`}>
                {msg.file_url && msg.file_type === 'image' ? (
                  <div className="mb-1">
                    <a href={`${BACKEND_URL}${msg.file_url}`} target="_blank" rel="noopener noreferrer">
                      <img 
                        src={`${BACKEND_URL}${msg.file_url}`} 
                        alt={msg.file_name || 'image'} 
                        className="max-w-full rounded-lg max-h-48 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                        data-testid="chat-image-preview"
                      />
                    </a>
                    <p className={`text-xs mt-1 ${isMe ? 'text-navy-200/70' : 'text-slate-400'}`}>{msg.file_name}</p>
                  </div>
                ) : msg.file_url && msg.file_type === 'file' ? (
                  <a 
                    href={`${BACKEND_URL}${msg.file_url}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={`flex items-center gap-2 py-1 ${isMe ? 'text-navy-200 hover:text-navy-100' : 'text-gold hover:text-amber-400'} transition-colors`}
                    data-testid="chat-file-link"
                  >
                    <FileText className="w-5 h-5 flex-shrink-0" />
                    <span className="text-sm underline truncate">{msg.file_name}</span>
                    <Download className="w-4 h-4 flex-shrink-0" />
                  </a>
                ) : (
                  <p className="text-sm whitespace-pre-wrap break-words">{msg.message}</p>
                )}
                <p className={`text-[10px] mt-1 ${isMe ? 'text-navy-200/60' : 'text-slate-500'}`}>
                  {formatTime(msg.created_at)}
                  {isMe && msg.is_read && ' ✓✓'}
                </p>
              </div>
            </div>
          );
        })}
        {/* Typing Indicator */}
        {isOtherTyping && (
          <div className="flex justify-start" data-testid="typing-indicator">
            <div className="bg-navy-100 border border-slate-700/50 rounded-2xl px-4 py-2.5 flex items-center gap-1.5">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-gold rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                <span className="w-2 h-2 bg-gold rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                <span className="w-2 h-2 bg-gold rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
              </div>
              <span className="text-slate-400 text-xs ml-1">
                {currentRole === 'partner' ? 'η J.HOUSEOFSECRETARY γράφει' : `${partnerName || partnerUsername} γράφει`}...
              </span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t border-slate-700/50 p-3 bg-navy-200/80 rounded-b-xl">
        {uploading && (
          <div className="flex items-center gap-2 mb-2 px-2">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-gold border-t-transparent"></div>
            <span className="text-xs text-slate-400">Ανέβασμα αρχείου...</span>
          </div>
        )}
        <div className="flex items-end gap-2 relative">
          {showEmoji && <EmojiPicker onSelect={(emoji) => setInput(prev => prev + emoji)} onClose={() => setShowEmoji(false)} />}
          <button onClick={() => setShowEmoji(!showEmoji)} className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-slate-700/50 text-slate-400 hover:text-gold transition-colors" data-testid="emoji-toggle-btn">
            <Smile className="w-5 h-5" />
          </button>
          <button 
            onClick={() => fileInputRef.current?.click()} 
            disabled={uploading}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-slate-700/50 text-slate-400 hover:text-gold transition-colors disabled:opacity-40"
            data-testid="chat-attach-btn"
          >
            <Paperclip className="w-5 h-5" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip"
            onChange={handleFileUpload}
          />
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={handleInputChange}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            placeholder="Γράψτε μήνυμα..."
            className="flex-1 px-4 py-2.5 bg-navy-100/50 border border-slate-700 rounded-full text-white text-sm placeholder-slate-500 focus:outline-none focus:border-gold/50"
            data-testid="chat-message-input"
          />
          <button
            onClick={sendMessage}
            disabled={!input.trim() || sending}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-gold hover:bg-amber-500 text-navy-200 transition-colors disabled:opacity-40 disabled:hover:bg-gold"
            data-testid="chat-send-message-btn"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== FLOATING CHAT BUBBLE (Partner) ====================
export const PartnerChatBubble = ({ username }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const response = await axios.get(`${API}/chat/unread/${username}`);
        setUnreadCount(response.data.unread_count || 0);
      } catch (err) {
        console.error('Error fetching unread count:', err);
      }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 5000);
    return () => clearInterval(interval);
  }, [username]);

  return (
    <div className="fixed bottom-6 right-6 z-[9998]" data-testid="partner-chat-bubble">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            className="absolute bottom-16 right-0 w-80 sm:w-96 rounded-xl overflow-hidden shadow-2xl border border-gold/20"
          >
            <ChatWindow
              currentUser={username}
              currentRole="partner"
              partnerUsername="admin"
              partnerName="J. House of Secretary"
              onClose={() => setIsOpen(false)}
              isFloating={true}
            />
          </motion.div>
        )}
      </AnimatePresence>
      <button
        onClick={() => { setIsOpen(!isOpen); if (!isOpen) setUnreadCount(0); }}
        className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all ${
          isOpen ? 'bg-slate-800 border border-slate-600' : 'bg-gradient-to-br from-gold to-amber-600'
        }`}
        data-testid="partner-chat-toggle"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <>
            <MessageCircle className="w-6 h-6 text-navy-200" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </>
        )}
      </button>
    </div>
  );
};

// ==================== ADMIN CHAT PANEL ====================
export const AdminChatPanel = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [totalUnread, setTotalUnread] = useState(0);

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/chat/admin/conversations`);
      const convs = response.data.conversations || [];
      setConversations(convs);
      setTotalUnread(convs.reduce((sum, c) => sum + (c.unread_count || 0), 0));
    } catch (err) {
      console.error('Error fetching conversations:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConversations();
    const interval = setInterval(fetchConversations, 5000);
    return () => clearInterval(interval);
  }, []);

  const formatTime = (isoString) => {
    if (!isoString) return '';
    const d = new Date(isoString);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    if (isToday) return d.toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit' });
  };

  if (selectedChat) {
    return (
      <div className="bg-navy-100/50 rounded-xl border border-slate-700/50 overflow-hidden" data-testid="admin-chat-conversation">
        <ChatWindow
          currentUser="admin"
          currentRole="admin"
          partnerUsername={selectedChat.partner_username}
          partnerName={selectedChat.partner_name}
          onClose={() => { setSelectedChat(null); fetchConversations(); }}
        />
      </div>
    );
  }

  return (
    <div className="bg-navy-100/50 rounded-xl border border-slate-700/50 p-6" data-testid="admin-chat-panel">
      <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
        <MessageCircle className="w-5 h-5 text-gold" />
        Μηνύματα
        {totalUnread > 0 && (
          <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">{totalUnread}</span>
        )}
      </h3>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-gold border-t-transparent"></div>
        </div>
      ) : conversations.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Δεν υπάρχουν συνομιλίες</p>
        </div>
      ) : (
        <div className="space-y-2">
          {conversations.map((conv) => (
            <div
              key={conv.partner_username}
              onClick={() => setSelectedChat(conv)}
              className={`p-4 rounded-xl border cursor-pointer transition-all hover:scale-[1.01] ${
                conv.unread_count > 0
                  ? 'bg-gold/5 border-gold/30 hover:bg-gold/10'
                  : 'bg-navy-200/30 border-slate-700/50 hover:bg-navy-200/50'
              }`}
              data-testid={`chat-conv-${conv.partner_username}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                    conv.unread_count > 0 ? 'bg-gold/20 text-gold' : 'bg-slate-700/50 text-slate-300'
                  }`}>
                    {(conv.partner_name || conv.partner_username || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={`font-medium text-sm truncate ${conv.unread_count > 0 ? 'text-white' : 'text-slate-300'}`}>
                        {conv.partner_name || conv.partner_username}
                      </p>
                      {conv.unread_count > 0 && (
                        <span className="px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full">{conv.unread_count}</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 truncate mt-0.5">
                      {conv.last_sender === 'admin' ? 'Εσείς: ' : ''}{conv.last_message}
                    </p>
                  </div>
                </div>
                <span className="text-[10px] text-slate-500 ml-2 whitespace-nowrap">{formatTime(conv.last_time)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
